import { Check, ChevronDown, ChevronLeft, ChevronRight } from "lucide-react";
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { useBelow } from "../../lib/use-breakpoint";
import { C, NAV_SEL_EDGE, NAV_SEL_FILL, TYPE } from "./tokens";

/**
 * Shared "step deck" navigation for the Go-to-Market deep dives (3.2
 * Distribution · 4.1 Connectivity · 4.2 Visa).
 *
 * The prototype's model: instead of stacking every step in one long scroll (so
 * the in-page nav row scrolls out of reach), the steps live in a single
 * horizontal rail that rides *inside* the section's already-sticky command bar
 * — so it pins to the top and can never get lost — and only the active step's
 * panel is shown at a time. These are plain navigation TABS, not a wizard:
 * there is no "done" state and no completion tick — any tab is reachable at any
 * time. A Prev/Next pager (and ←/→ arrow keys) is a convenience for reading in
 * order; the rail lets you jump anywhere.
 *
 * Rendered identically across all three sections so the idiom never drifts.
 */

export interface StepDeckItem {
  key: string;
  label: string;
  /** Optional display index (e.g. "1.1"). Falls back to the padded ordinal
   *  ("01", "02", …) when absent — GTM deep dives rely on that fallback. */
  num?: string;
}

/**
 * Controls the active index. `count` clamps the index when the step list
 * shrinks; `resetKey` (e.g. the report's market ISO) snaps back to the first
 * step whenever it changes, so a new market always opens on step 01.
 */
export function useStepDeck(count: number, resetKey?: string) {
  const [active, setActive] = useState(0);

  // Snap to the first step on a fresh report.
  // biome-ignore lint/correctness/useExhaustiveDependencies: reset is keyed on resetKey by design.
  useEffect(() => {
    setActive(0);
  }, [resetKey]);

  // Clamp when the step count shrinks (a market with fewer steps).
  useEffect(() => {
    setActive((a) => Math.min(a, Math.max(0, count - 1)));
  }, [count]);

  const go = useCallback((i: number) => setActive(Math.max(0, Math.min(count - 1, i))), [count]);
  const next = useCallback(() => go(active + 1), [go, active]);
  const prev = useCallback(() => go(active - 1), [go, active]);

  return { active, setActive: go, next, prev };
}

/** The horizontal numbered rail — lives inside the sticky command bar. */
export function StepRail({
  steps,
  active,
  onSelect,
}: {
  steps: StepDeckItem[];
  active: number;
  onSelect: (i: number) => void;
}) {
  const refs = useRef<(HTMLButtonElement | null)[]>([]);
  const railRef = useRef<HTMLDivElement | null>(null);
  // On phone the equal-segment rail can't hold long labels without a sideways
  // scroll (which hides steps + reads as "shrunk desktop"). Below the mobile
  // breakpoint we swap to a dropdown so every step is one tap away, no scroll.
  const isPhone = useBelow("mobile");

  // ── Fit-to-width rule ──────────────────────────────────────────────────────
  // A tab label must NEVER truncate (a core client complaint: labels clipped to
  // "…" at narrow widths / high browser zoom, and everyone at a different zoom
  // saw something different). Because these are equal-width segments, one long
  // label would clip while the rest have room — so the copy is never rewritten;
  // instead ALL labels on the rail shrink to ONE uniform size (a single CSS var,
  // so no tab is ever a different size from its neighbour on the same page) just
  // enough for the WHOLE rail to fit its container on one line. More steps, or
  // longer step names, therefore mean smaller text automatically. If even the
  // floor (11px) can't fit, the labels wrap to a second line rather than clip.
  // Measured, not guessed, so it re-fits on resize, zoom, and web-font load.
  // All ~30 sections that mount this shared rail inherit the rule at once.
  const labelsKey = steps.map((s) => s.label).join("|");
  // biome-ignore lint/correctness/useExhaustiveDependencies: labelsKey re-runs the fit when the step labels change (the effect reads them via the DOM, not the closure).
  useLayoutEffect(() => {
    const rail = railRef.current;
    if (!rail || isPhone) return;
    const BASE = 13;
    const FLOOR = 11;
    let lastWidth = -1;
    let timer = 0;
    let cancelled = false;

    const setSize = (px: number) => rail.style.setProperty("--gsd-label-size", `${px.toFixed(2)}px`);

    // Does the WHOLE rail fit on one line at the size currently applied? The
    // question has to be asked of the rail, not of the individual labels: the
    // segments carry `min-width: max-content`, so they never shrink and never
    // clip — a label always reports scrollWidth === clientWidth, however far
    // the rail is overflowing. (That's what made the old per-label test dead
    // code in exactly the long-label case it was written for: it measured a
    // clip that, by construction, can't happen.)
    // Summed segment widths, not rail.scrollWidth vs rail.clientWidth: with
    // overflowing flex content Chrome leaves the container's trailing padding
    // out of scrollWidth, which under-reports the overflow and lets the last
    // tab sit flush against the border.
    const fitsOnOneLine = () => {
      const steps = rail.querySelectorAll<HTMLElement>(".gsd-step");
      if (steps.length === 0) return true;
      const cs = getComputedStyle(rail);
      const gap = Number.parseFloat(cs.columnGap) || 0;
      const padX = (Number.parseFloat(cs.paddingLeft) || 0) + (Number.parseFloat(cs.paddingRight) || 0);
      let need = gap * (steps.length - 1);
      for (const el of steps) need += el.getBoundingClientRect().width;
      return need <= rail.clientWidth - padX + 1; // 1px for sub-pixel rounding
    };

    const fit = () => {
      // Always measure from the base size, single line — so the computation is
      // stable no matter what size is currently applied.
      rail.classList.remove("gsd-fit-wrap");
      setSize(BASE);
      if (fitsOnOneLine()) return; // everything already fits at base
      setSize(FLOOR);
      if (!fitsOnOneLine()) {
        // Can't fit on one line even at the floor — hold the floor and wrap.
        rail.classList.add("gsd-fit-wrap");
        return;
      }
      // Largest size in [FLOOR, BASE] that still fits. Searched, not computed
      // from a width ratio: the rail's width is not proportional to the label
      // size (the segment padding and the gaps between them don't scale with
      // it), so a ratio overshoots. 6 passes land within ~0.03px of the true
      // maximum, and each pass is one layout read on a handful of nodes.
      let lo = FLOOR;
      let hi = BASE;
      for (let i = 0; i < 6; i++) {
        const mid = (lo + hi) / 2;
        setSize(mid);
        if (fitsOnOneLine()) lo = mid;
        else hi = mid;
      }
      setSize(lo);
    };
    // Coalesce bursts of resize/font callbacks with a short debounce. Uses a
    // timer, NOT requestAnimationFrame: rAF is starved indefinitely when the tab
    // isn't actively painting (background tab, headless render), which would
    // leave the rail stuck at a stale width's fit. A timer always fires.
    const schedule = () => {
      window.clearTimeout(timer);
      timer = window.setTimeout(fit, 60);
    };

    // Run the first pass synchronously: this effect is a useLayoutEffect, so
    // layout is already available and we want the correct size on first paint,
    // never a base-size flash that re-fits a frame later.
    fit();
    // Only re-fit on WIDTH changes — applying a wrap changes the rail's HEIGHT,
    // and reacting to that would loop.
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect.width ?? 0;
      if (Math.abs(w - lastWidth) < 0.5) return;
      lastWidth = w;
      schedule();
    });
    ro.observe(rail);
    // Web-font metrics differ from the fallback → re-fit once fonts settle.
    document.fonts?.ready.then(() => {
      if (!cancelled) schedule();
    });
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
      ro.disconnect();
    };
  }, [labelsKey, isPhone]);

  const onKeyDown = (e: React.KeyboardEvent) => {
    let to = active;
    if (e.key === "ArrowRight" || e.key === "ArrowDown") to = Math.min(steps.length - 1, active + 1);
    else if (e.key === "ArrowLeft" || e.key === "ArrowUp") to = Math.max(0, active - 1);
    else if (e.key === "Home") to = 0;
    else if (e.key === "End") to = steps.length - 1;
    else return;
    e.preventDefault();
    onSelect(to);
    refs.current[to]?.focus();
  };

  if (isPhone) return <StepDropdown steps={steps} active={active} onSelect={onSelect} />;

  return (
    <div className="gsd-rail" ref={railRef} role="tablist" aria-label="Analysis steps" onKeyDown={onKeyDown}>
      <div className="gsd-track" aria-hidden="true" />
      {steps.map((s, i) => (
        <button
          key={s.key}
          ref={(el) => {
            refs.current[i] = el;
          }}
          type="button"
          role="tab"
          aria-selected={i === active}
          tabIndex={i === active ? 0 : -1}
          className={`gsd-step ${i === active ? "is-active" : ""}`}
          onClick={() => onSelect(i)}
        >
          <span className="gsd-num">{s.num ?? String(i + 1).padStart(2, "0")}</span>
          <span className="gsd-label">{s.label}</span>
        </button>
      ))}
      <StepDeckStyles />
    </div>
  );
}

/** Phone variant of the rail — the same tabs, presented as a dropdown so long
 *  step labels never force a sideways scroll. Any step is one tap away. */
function StepDropdown({
  steps,
  active,
  onSelect,
}: {
  steps: StepDeckItem[];
  active: number;
  onSelect: (i: number) => void;
}) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const numOf = (s: StepDeckItem, i: number) => s.num ?? String(i + 1).padStart(2, "0");
  const cur = steps[active] ?? steps[0]!;
  const atStart = active <= 0;
  const atEnd = active >= steps.length - 1;

  return (
    <div className="gsd-dd" ref={wrapRef}>
      {/* Compact stepper: the dropdown (jump to any step) flanked by prev/next
          arrows (step one at a time without opening the menu). The dropdown alone
          hides the "advance" affordance the segmented rail gives for free; the
          arrows restore it. Disabled at the two ends so the bounds are legible. */}
      <div className="gsd-dd-pager">
        <button
          type="button"
          className="gsd-dd-arrow"
          aria-label="Previous step"
          disabled={atStart}
          onClick={() => onSelect(active - 1)}
        >
          <ChevronLeft size={18} strokeWidth={2.2} aria-hidden="true" />
        </button>
        <button
          type="button"
          className="gsd-dd-btn"
          aria-haspopup="listbox"
          aria-expanded={open}
          onClick={() => setOpen((o) => !o)}
        >
          <span className="gsd-dd-num">{numOf(cur, active)}</span>
          <span className="gsd-dd-label">{cur.label}</span>
          <span className="gsd-dd-meta">
            {active + 1}/{steps.length}
          </span>
          <ChevronDown className="gsd-dd-chev" data-open={open} size={16} strokeWidth={2} aria-hidden="true" />
        </button>
        <button
          type="button"
          className="gsd-dd-arrow"
          aria-label="Next step"
          disabled={atEnd}
          onClick={() => onSelect(active + 1)}
        >
          <ChevronRight size={18} strokeWidth={2.2} aria-hidden="true" />
        </button>
      </div>
      {open && (
        // A native <select>/<option> can't carry this item markup (index chip +
        // label + check glyph) or the panel styling, so the listbox is built from
        // ARIA. tabIndex makes the container focusable as the rule requires;
        // arrow-key handling lives on the rail above.
        <div
          className="gsd-dd-menu"
          aria-label="Analysis steps"
          tabIndex={-1}
          // biome-ignore lint/a11y/useSemanticElements: custom dropdown — see above
          role="listbox"
        >
          {steps.map((s, i) => (
            <button
              key={s.key}
              type="button"
              // biome-ignore lint/a11y/useSemanticElements: option inside the ARIA listbox above
              role="option"
              aria-selected={i === active}
              className={`gsd-dd-item ${i === active ? "is-active" : ""}`}
              onClick={() => {
                onSelect(i);
                setOpen(false);
              }}
            >
              <span className="gsd-dd-inum">{numOf(s, i)}</span>
              <span className="gsd-dd-ilabel">{s.label}</span>
              {i === active && <Check className="gsd-dd-chk" size={15} strokeWidth={2.4} aria-hidden="true" />}
            </button>
          ))}
        </div>
      )}
      <StepDeckStyles />
    </div>
  );
}

function StepDeckStyles() {
  return (
    <style>{`
      /* ── The rail — a SEGMENTED CONTROL: a recessed track holding equal
            segments, with the active one raised as a blue-tinted chip. Plain
            navigation between panels; no order, no completion. ── */
      .gsd-rail {
        position: relative;
        display: flex;
        align-items: stretch;
        gap: 4px;
        width: 100%;
        padding: 4px;
        border-radius: 12px;
        border: 1px solid ${C.border};
        background: ${C.mute};
        overflow-x: auto;
        scrollbar-width: none;
        scroll-snap-type: x proximity;
      }
      .gsd-rail::-webkit-scrollbar { display: none; }
      /* No baseline track in segmented mode — the container is the track. */
      .gsd-track { display: none; }
      .gsd-step {
        appearance: none;
        -webkit-appearance: none;
        margin: 0;
        cursor: pointer;
        position: relative;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        gap: 8px;
        padding: 8px 12px;
        flex: 1 1 0;
        min-width: max-content;
        white-space: nowrap;
        scroll-snap-align: start;
        border-radius: 8px;
        border: 1px solid transparent;
        background: transparent;
        transition: background .18s ease, border-color .18s ease, box-shadow .18s ease;
      }
      .gsd-step:hover:not(.is-active) { background: color-mix(in srgb, ${C.textHi} 6%, transparent); }
      .gsd-step:focus-visible {
        outline: none;
        box-shadow: 0 0 0 2px color-mix(in srgb, ${C.textHi} 45%, transparent);
      }
      /* The raised active segment — a decisively brighter NEUTRAL-GREY chip so
         the selected panel is unmistakable against an idle one. Hue-less by
         design: selection reads identically in every workspace, NOT the
         workspace accent (which still colours this section's content + kicker
         via --rail-accent / --ds-kick — we only stop reading it HERE). */
      .gsd-step.is-active {
        background: color-mix(in srgb, ${C.textHi} 18%, ${C.surface});
        border-color: ${NAV_SEL_EDGE};
        box-shadow: 0 1px 3px rgba(0,0,0,0.30), inset 0 1px 0 color-mix(in srgb, ${C.textHi} 40%, transparent);
      }

      /* Number = a subordinate index, not a badge. Mono instrument label.
         Idle runs at full ink (textHi), NOT a dimmed grey: this rail is mono +
         UPPERCASE + wide-tracked, which already reads harder than the left tree's
         14px mixed-case sans, so it can't also afford a dim colour. State is
         carried by the raised active chip, not by greying the idle tabs. */
      .gsd-num {
        flex: 0 0 auto;
        font-family: ${TYPE.label.fontFamily};
        /* Scales with its label off the SAME variable — the index is part of
           the tab's text, and leaving it at a fixed 13px next to an 11px label
           both looks wrong and holds width the fit rule is trying to recover. */
        font-size: var(--gsd-label-size, 13px);
        font-weight: ${TYPE.label.fontWeight};
        letter-spacing: ${TYPE.label.letterSpacing};
        color: ${C.textHi};
        transition: color .18s ease;
      }
      .gsd-step.is-active .gsd-num { color: ${C.textHi}; }

      .gsd-label {
        font-family: ${TYPE.label.fontFamily};
        /* Size is driven by the fit-to-width rule in StepRail: all labels on the
           rail share this one variable, so they shrink UNIFORMLY to fit and no
           label ever clips to "…". Falls back to the base 13px before measure. */
        font-size: var(--gsd-label-size, 13px);
        font-weight: ${TYPE.label.fontWeight};
        letter-spacing: ${TYPE.label.letterSpacing};
        text-transform: uppercase;
        min-width: 0;
        overflow: hidden;
        text-overflow: ellipsis;
        color: ${C.textHi};
        transition: color .18s ease;
      }
      /* The selected tab already sits on a raised blue chip; a heavier weight (600
         vs the idle 400) keeps it distinct now that idle is full-bright too. */
      .gsd-step.is-active .gsd-label { font-weight: 600; }

      /* Last-resort of the fit rule: when even the floor size can't fit one line,
         labels WRAP to a second line rather than truncate. A single word longer
         than its segment (e.g. PRIORITIZATION) can't wrap on whitespace, so
         overflow-wrap break-word lets it break mid-word rather than spill past
         its box and get clipped by the rail — the whole point is that copy is
         never cut without recourse.
         Releasing the SEGMENT's min-width is what makes the wrap possible:
         max-content is measured on a single line, so a segment still demanding
         it would stay full width and the label would have nothing to wrap
         into — the rail would keep overflowing at the floor size. */
      .gsd-rail.gsd-fit-wrap .gsd-step { min-width: 0; }
      .gsd-rail.gsd-fit-wrap .gsd-label {
        overflow: visible;
        text-overflow: clip;
        white-space: normal;
        /* overflow-wrap alone, NOT word-break: break-word — the latter is an
           alias of "anywhere", which lets the browser break INSIDE a word even
           when that word would fit on a line of its own ("COMPARABL / E
           ANALYSIS"). overflow-wrap only breaks a word that cannot fit its box
           at all, which is the case this fallback actually needs to survive. */
        overflow-wrap: break-word;
        line-height: 1.12;
        text-align: center;
      }

      @media (max-width: 640px) {
        /* Too tight for equal segments — let each segment size to its label and
           scroll the control horizontally. */
        .gsd-step { flex: 0 0 auto; min-width: max-content; }
        .gsd-label { overflow: visible; }
      }
      /* ── Phone dropdown variant (replaces the rail below the mobile breakpoint) ──
            A full-width trigger showing the current step + an opaque popover list,
            so long labels never force a sideways scroll and every step is one tap. */
      .gsd-dd { position: relative; width: 100%; }
      /* The pager row: [‹] [ dropdown trigger ] [›]. Arrows stretch to the
         trigger's height (align-items: stretch) so the control reads as one unit. */
      .gsd-dd-pager {
        display: flex;
        align-items: stretch;
        gap: 6px;
        width: 100%;
      }
      .gsd-dd-btn {
        appearance: none; -webkit-appearance: none; margin: 0;
        flex: 1 1 auto;
        min-width: 0;
        display: flex;
        align-items: center;
        gap: 10px;
        padding: 11px 12px;
        border-radius: 12px;
        border: 1px solid ${C.border};
        background: ${C.mute};
        color: ${C.textHi};
        cursor: pointer;
        text-align: left;
      }
      /* Prev/next steppers — quiet by default, so the dropdown stays the primary
         target; they light up on hover and go faint + non-interactive at the ends. */
      .gsd-dd-arrow {
        appearance: none; -webkit-appearance: none; margin: 0;
        flex: 0 0 auto;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 42px;
        border-radius: 12px;
        border: 1px solid ${C.border};
        background: ${C.mute};
        color: ${C.textMd};
        cursor: pointer;
        transition: background .18s ease, color .18s ease, border-color .18s ease, opacity .18s ease;
      }
      .gsd-dd-arrow:hover:not(:disabled) {
        background: color-mix(in srgb, ${C.textHi} 6%, transparent);
        color: ${C.textHi};
        border-color: ${C.borderStrong};
      }
      .gsd-dd-arrow:focus-visible {
        outline: none;
        box-shadow: 0 0 0 2px color-mix(in srgb, ${C.textHi} 45%, transparent);
      }
      .gsd-dd-arrow:disabled { opacity: 0.35; cursor: default; }
      .gsd-dd-num {
        flex: 0 0 auto;
        font-family: ${TYPE.label.fontFamily};
        font-size: 13px;
        font-weight: ${TYPE.label.fontWeight};
        letter-spacing: ${TYPE.label.letterSpacing};
        color: ${C.textHi};
      }
      .gsd-dd-label {
        flex: 1 1 auto;
        min-width: 0;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        font-family: ${TYPE.label.fontFamily};
        font-size: 13px;
        font-weight: 600;
        letter-spacing: ${TYPE.label.letterSpacing};
        text-transform: uppercase;
        color: ${C.textHi};
      }
      .gsd-dd-meta {
        flex: 0 0 auto;
        font-family: ${TYPE.label.fontFamily};
        font-size: 11px;
        letter-spacing: 0.1em;
        color: ${C.textLo};
        font-variant-numeric: tabular-nums;
      }
      .gsd-dd-chev { flex: 0 0 auto; color: ${C.textMd}; transition: transform 0.18s ease; }
      .gsd-dd-chev[data-open="true"] { transform: rotate(180deg); }
      .gsd-dd-menu {
        position: absolute;
        top: calc(100% + 6px);
        left: 0;
        right: 0;
        z-index: 60;
        background: ${C.tooltipBg};
        border: 1px solid ${C.borderStrong};
        border-radius: 12px;
        padding: 6px;
        box-shadow: 0 18px 44px rgba(0, 0, 0, 0.5);
        max-height: 60vh;
        overflow-y: auto;
      }
      .gsd-dd-item {
        appearance: none; -webkit-appearance: none; margin: 0;
        width: 100%;
        display: flex;
        align-items: center;
        gap: 12px;
        padding: 12px 10px;
        border: 0;
        background: transparent;
        border-radius: 8px;
        cursor: pointer;
        text-align: left;
        color: ${C.textMd};
      }
      .gsd-dd-item:hover { background: color-mix(in srgb, ${C.textHi} 6%, transparent); }
      .gsd-dd-item.is-active { background: ${NAV_SEL_FILL}; }
      .gsd-dd-inum {
        flex: 0 0 auto;
        width: 26px;
        font-family: ${TYPE.label.fontFamily};
        font-size: 12px;
        letter-spacing: 0.06em;
        color: ${C.textLo};
      }
      .gsd-dd-item.is-active .gsd-dd-inum { color: ${C.textHi}; }
      .gsd-dd-ilabel {
        flex: 1 1 auto;
        min-width: 0;
        font-family: ${TYPE.label.fontFamily};
        font-size: 13px;
        letter-spacing: ${TYPE.label.letterSpacing};
        text-transform: uppercase;
        color: ${C.textHi};
      }
      .gsd-dd-chk { flex: 0 0 auto; margin-left: auto; color: var(--intel-accent, ${C.accent}); }

      @media (prefers-reduced-motion: reduce) {
        .gsd-step, .gsd-num, .gsd-label, .gsd-dd-chev, .gsd-dd-arrow { transition: none; }
      }
    `}</style>
  );
}
